import { aiMessages } from '@atlaskit/editor-common/ai-messages';
import { EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI, PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import { isEmptyDocument } from '@atlaskit/editor-common/utils';
import { fg } from '@atlaskit/platform-feature-flags';

import type { AIPlugin } from '../../aiPluginType';
import type { AIGlobalOptIn, AISmartButtonOptions, GetMentionNameDetails } from '../../types/types';
import { CONFIG_ITEM_KEYS } from '../../ui/prebuilt/config-items/config-item-keys';
import type { EditorPluginAIConfigItemWithOptions } from '../../ui/prebuilt/config-items/config-items';
import { addAnalytics } from '../../ui/utils/analytics/utils';

import { aiSmartButtonAPIKey } from './ai-smart-button-api-plugin-key';
import { removeSelectionCommand } from './commands';
import { FullPageImproveWritingNudger } from './nudge/full-page-improve-writing';
import { createInitialState, createPluginState } from './plugin-factory';
import { createGetNudgeFromConfigItem } from './utils/create-nudge-from-config-item';
import {
	chooseActionsToShowForSelection,
	getActionsListForSelection,
} from './utils/map-config-items-to-actions';

export const createAISmartButtonAPIPlugin = ({
	aiGlobalOptIn,
	aiSmartButtonOptions,
	dispatch,
	dynamicSurfaceConfigItems,
	getIntl,
	proactiveAIApiUrl,
	product,
	// Optional options
	api,
	getMentionNameDetails,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	aiSmartButtonOptions: AISmartButtonOptions;
	dispatch: PMPluginFactoryParams['dispatch'];
	dynamicSurfaceConfigItems: EditorPluginAIConfigItemWithOptions[];
	getIntl: PMPluginFactoryParams['getIntl'];
	proactiveAIApiUrl: string;
	product: string;
	// Optional options
	api?: ExtractInjectionAPI<AIPlugin>;
	getMentionNameDetails?: GetMentionNameDetails;
}) => {
	const intl = getIntl();

	const improveWritingConfigItem = dynamicSurfaceConfigItems.find(
		(configItem) => configItem.config.key === CONFIG_ITEM_KEYS.IMPROVE_WRITING,
	);

	const { setActions, removeAll, removeContext, setContext } = aiSmartButtonOptions.actions;

	function isInEditMode() {
		const editorViewModeState = api?.editorViewMode?.sharedState.currentState();
		const mode = editorViewModeState?.mode;
		const isInEditMode = !!(mode && mode === 'edit');
		return isInEditMode;
	}

	return new SafePlugin({
		state: createPluginState(dispatch, createInitialState()),
		key: aiSmartButtonAPIKey,
		view: (editorView) => {
			const { emptyDocActions, nonEmptyDocActions, selectionActions } = getActionsListForSelection({
				aiGlobalOptIn,
				dynamicSurfaceConfigItems,
				intl,
				view: editorView,
			});

			// Track the edit mode state
			let wasInEditMode = isInEditMode();

			// Track the doc state and selection state to avoid resetting actions unnecessarily
			let wasDocumentEmpty = isEmptyDocument(editorView.state.doc);
			let wasSelectionEmpty = editorView.state.selection.empty;

			// Register editor actions only in edit page or edit mode of live page.
			if (wasInEditMode) {
				setActions(
					chooseActionsToShowForSelection(
						{
							isEmptyDoc: isEmptyDocument(editorView.state.doc),
							isEmptySelection: editorView.state.selection.empty,
						},
						{
							emptyDocActions,
							nonEmptyDocActions,
							selectionActions,
						},
					),
				);
			}

			const reference = intl.formatMessage(aiMessages.aiSmartButtonDynamicSurfaceSelectedContent);
			/**
			 * On removing reference, remove the selection and focus the editor.
			 */
			const onRemoveReference = () => {
				removeSelectionCommand({ aiSmartButtonOptions })(editorView.state, editorView.dispatch);
				editorView.focus();
			};

			let improveWritingNudger: FullPageImproveWritingNudger | undefined;
			if (fg('platform_editor_ai_proactive_ai_nudge')) {
				const getImproveWritingNudge =
					improveWritingConfigItem &&
					createGetNudgeFromConfigItem({
						configItem: improveWritingConfigItem,
						intl,
						aiGlobalOptIn,
						editorView,
					});

				improveWritingNudger =
					getImproveWritingNudge &&
					new FullPageImproveWritingNudger(aiSmartButtonOptions, {
						locale: intl.locale,
						getNudge: getImproveWritingNudge,
						onCollectMetrics: (nudgeMetrics) => {
							const tr = addAnalytics({
								editorState: editorView.state,
								tr: editorView.state.tr,
								payload: {
									action: 'collected',
									actionSubject: 'editorPluginAI',
									actionSubjectId: 'nudgeMetrics',
									attributes: {
										nudgeMetrics,
									},
									eventType: EVENT_TYPE.OPERATIONAL,
								},
							});
							editorView.dispatch(tr);
						},
						proactiveAIApiUrl,
						product: product,
						view: editorView,
						// Optional options
						api,
						getMentionNameDetails,
					});

				improveWritingNudger?.start();
			}

			function cleanup() {
				removeAll();
				removeContext();
				improveWritingNudger?.stop();
			}

			return {
				update: (editorView, prevState) => {
					const currentEditorState = editorView.state;
					const currentlyInEditMode = isInEditMode();
					const switchedFromViewToEdit = !wasInEditMode && currentlyInEditMode;

					/**
					 * Switching from edit to view mode, we need to remove
					 * 	all actions, context and nudges.
					 */
					if (wasInEditMode && !currentlyInEditMode) {
						cleanup();
					}
					wasInEditMode = currentlyInEditMode;

					if (!currentlyInEditMode) {
						return;
					}

					/**
					 * Switching from view mode to edit mode, editor state doesn't change.
					 * But editing actions must be added again as it had been removed
					 * 	when transitioning to edito -> view mode.
					 */
					if (
						currentEditorState.doc.eq(prevState.doc) &&
						currentEditorState.selection.empty === prevState.selection.empty &&
						!switchedFromViewToEdit
					) {
						return;
					}

					/**
					 * Update;
					 * - registry of editor actions
					 * - context on selection change
					 * - nudges related stuff
					 * only in edit page or edit mode of live page.
					 */
					const isCurrentSelectionEmpty = currentEditorState.selection.empty;
					const isCurrentDocEmpty = isEmptyDocument(currentEditorState.doc);

					// only reset actions if the selection or document state has changed
					// this avoids unnecessary re-renders for editor
					if (
						isCurrentSelectionEmpty !== wasSelectionEmpty ||
						isCurrentDocEmpty !== wasDocumentEmpty
					) {
						setActions(
							chooseActionsToShowForSelection(
								{ isEmptyDoc: isCurrentDocEmpty, isEmptySelection: isCurrentSelectionEmpty },
								{
									emptyDocActions,
									nonEmptyDocActions,
									selectionActions,
								},
							),
						);

						wasSelectionEmpty = isCurrentSelectionEmpty;
						wasDocumentEmpty = isCurrentDocEmpty;
					}

					/**
					 * Check for dynamic surface context change on every selection change.
					 * Not using onSelectionChanged in pluginFactory because
					 * it will be called only when selection changes by current user.
					 */
					if (!currentEditorState.selection.empty) {
						setContext({
							reference,
							onRemove: onRemoveReference,
						});
					} else {
						removeContext();
					}

					if (improveWritingNudger && !editorView.state.doc.eq(prevState.doc)) {
						improveWritingNudger.setLastDocChangeTime(Date.now());
					}
				},
				destroy: () => {
					cleanup();
				},
			};
		},
	});
};
